import { makeAutoObservable, toJS } from 'mobx'

import {
  ResortSearch,
  TourDepartureCity,
  TourSearchDestination,
  TownTo,
} from 'shared/api/tours/search'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { DEFAULT_TOUR_SORT_OPTION } from 'shared/constants/tours'
import { dateFormat, getIsBeforeDateAndSame } from 'shared/lib/dateFormat'

import { OrderBy } from './models'

export interface DataForSearchTour {
  duration?: number
  resorts?: TownTo[]
  country?: TourSearchDestination
  flight?: {
    startDay: string
  }
  passengers?: {
    adults: number
    children?: number
    childrenAges?: number[]
  }
  is_flexible_date?: boolean
  is_flexible_nights?: boolean
  departure?: TourDepartureCity
}

export interface DataTour extends Partial<Omit<ResortSearch, 'town_to'>> {}

export const InitialSearchTour = {
  passengers: {
    adults: 2,
  },
  duration: 7,
  is_flexible_date: true,
  is_flexible_nights: true,
}

class SearchTours {
  dataTour?: DataTour = undefined // данные для формы поиска туров
  dataForSearchTour?: DataForSearchTour = InitialSearchTour // форма для поиска
  searchEditDraft?: DataForSearchTour = undefined
  orderBy: OrderBy = DEFAULT_TOUR_SORT_OPTION
  forMySelf?: boolean = false
  isTourLimited?: boolean = false

  constructor() {
    makeAutoObservable(this)
  }

  setOrderBy(orderBy: OrderBy) {
    this.orderBy = orderBy
  }

  getOrderBy() {
    return toJS(this.orderBy)
  }

  resetOrderBy() {
    this.orderBy = DEFAULT_TOUR_SORT_OPTION
  }

  setFormsDataTour(dataSearchTour?: DataTour) {
    this.dataTour = {
      ...this.dataTour,
      ...dataSearchTour,
    }
  }

  private setEditableDataForSearchTour(data?: DataForSearchTour) {
    if (this.searchEditDraft) {
      this.searchEditDraft = data

      return
    }

    this.dataForSearchTour = data
  }

  private getEditableData() {
    return this.searchEditDraft || this.dataForSearchTour
  }

  setStartFlight(flight?: DataForSearchTour['flight']) {
    this.setEditableDataForSearchTour({
      ...this.getEditableData(),
      flight,
    })
  }

  setPassengersCount(passengers?: DataForSearchTour['passengers']) {
    this.setEditableDataForSearchTour({
      ...this.getEditableData(),
      passengers,
    })
  }

  setDurationResort(durations?: number) {
    this.setEditableDataForSearchTour({
      ...this.getEditableData(),
      duration: durations,
    })
  }

  setDirectionResort(resorts: TownTo[]) {
    const dataForSearchTour = this.getEditableData()

    this.setEditableDataForSearchTour({
      resorts: resorts,
      country: dataForSearchTour?.country,
      passengers: dataForSearchTour?.passengers,
      duration: dataForSearchTour?.duration,
      departure: dataForSearchTour?.departure,
      is_flexible_date: dataForSearchTour?.is_flexible_date,
      is_flexible_nights: dataForSearchTour?.is_flexible_nights,
    })
  }

  setCountryDirection(country: DataForSearchTour['country']) {
    const dataForSearchTour = this.getEditableData()

    this.setEditableDataForSearchTour({
      ...dataForSearchTour,
      country,
      resorts:
        dataForSearchTour?.country?.id === country?.id
          ? dataForSearchTour?.resorts
          : undefined,
    })
  }

  setDeparture(departure?: DataForSearchTour['departure']) {
    this.setEditableDataForSearchTour({
      ...this.getEditableData(),
      departure: departure,
      country: undefined,
      resorts: undefined,
    })
  }

  getDataForSearchTour() {
    return toJS(this.dataForSearchTour)
  }

  getEditableDataForSearchTour() {
    return toJS(this.getEditableData())
  }

  beginSearchEdit() {
    this.searchEditDraft = toJS(this.dataForSearchTour)
  }

  commitSearchEdit() {
    if (!this.searchEditDraft) return

    this.dataForSearchTour = this.searchEditDraft
    this.searchEditDraft = undefined
  }

  cancelSearchEdit() {
    this.searchEditDraft = undefined
  }

  getIsSearchEditing() {
    return Boolean(this.searchEditDraft)
  }

  resetDataForSearchTour() {
    this.dataForSearchTour = InitialSearchTour
    this.searchEditDraft = undefined
  }

  getDataTour() {
    return toJS(this.dataTour)
  }

  getPassengers() {
    const passengers = this.dataForSearchTour?.passengers

    return toJS(passengers)
  }

  setDataForSearchTour(data: Partial<DataForSearchTour>) {
    this.setEditableDataForSearchTour({
      ...this.getEditableData(),
      ...data,
    })
  }

  onBuyForMySelf(variant: boolean) {
    this.forMySelf = variant
  }

  setIsTourLimited(isTourLimited: boolean) {
    this.isTourLimited = isTourLimited
  }

  getIsTourLimited() {
    return this.isTourLimited
  }

  setIsFlexibleDate(isFlexibleDate: boolean) {
    this.setEditableDataForSearchTour({
      ...this.getEditableData(),
      is_flexible_date: isFlexibleDate,
    })
  }

  getIsFlexibleDate() {
    return toJS(this.getEditableData()?.is_flexible_date)
  }

  setIsFlexibleNights(isFlexibleNights: boolean) {
    this.setEditableDataForSearchTour({
      ...this.getEditableData(),
      is_flexible_nights: isFlexibleNights,
    })
  }

  getIsFlexibleNights() {
    return toJS(this.getEditableData()?.is_flexible_nights)
  }

  setFormFromLocalStorage(data: Partial<DataForSearchTour>) {
    const currentDate = dateFormat(
      new Date().toISOString(),
      DATE_FORMAT_LIST.YEAR_FULL_DATE,
    )

    const isValidDate = getIsBeforeDateAndSame(
      currentDate,
      data?.flight?.startDay,
    )

    if (!isValidDate && data?.flight) data.flight.startDay = currentDate

    this.setDataForSearchTour(data)
  }
}

export default new SearchTours()
